import { getSkill, getSkillVersion, getSkillVersions } from "@/api/skills";
import type { ISkillFileSnapshot } from "@/api/skills/skills.types";
import { InlineDiffViewer } from "@/components/shared/InlineDiffViewer";
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    styled,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNowStrict } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import { sortSkillVersionsByRecency } from "./skill-version-order";

// ── Types ────────────────────────────────────────────────────────────

interface VersionDiffModalProps {
    open: boolean;
    onClose: () => void;
    skillId: number;
    initialVersionId?: number | null;
}

type DiffMode = "previous" | "current";

interface FileChange {
    name: string;
    status: "added" | "removed" | "modified";
    oldContent: string;
    newContent: string;
}

// ── Styled ───────────────────────────────────────────────────────────

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiBackdrop-root": {
        backgroundColor: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(4px)",
    },
    "& .MuiDialog-paper": {
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow:
            "0 20px 60px -12px rgba(0, 0, 0, 0.15), 0 8px 20px -8px rgba(0, 0, 0, 0.08)",
        height: "80vh",
    },
}));

// ── Helpers ──────────────────────────────────────────────────────────

function computeFileChanges(
    oldFiles: ISkillFileSnapshot[],
    newFiles: ISkillFileSnapshot[]
): FileChange[] {
    const changes: FileChange[] = [];
    const oldMap = new Map(oldFiles.map((f) => [f.name, f.content]));
    const newMap = new Map(newFiles.map((f) => [f.name, f.content]));

    // Added or modified
    for (const nf of newFiles) {
        const oldContent = oldMap.get(nf.name);
        if (oldContent === undefined) {
            changes.push({
                name: nf.name,
                status: "added",
                oldContent: "",
                newContent: nf.content,
            });
        } else if (oldContent !== nf.content) {
            changes.push({
                name: nf.name,
                status: "modified",
                oldContent,
                newContent: nf.content,
            });
        }
    }

    // Removed
    for (const of_ of oldFiles) {
        if (!newMap.has(of_.name)) {
            changes.push({
                name: of_.name,
                status: "removed",
                oldContent: of_.content,
                newContent: "",
            });
        }
    }

    return changes;
}

const STATUS_COLORS = {
    added: "success",
    removed: "error",
    modified: "info",
} as const;

const STATUS_LABELS: Record<FileChange["status"], string> = {
    added: "Added",
    removed: "Removed",
    modified: "Modified",
};

// ── Component ────────────────────────────────────────────────────────

export const VersionDiffModal = ({
    open,
    onClose,
    skillId,
    initialVersionId = null,
}: VersionDiffModalProps) => {
    const theme = useTheme();
    const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
        null
    );
    const [diffMode, setDiffMode] = useState<DiffMode>("previous");

    // Fetch version list
    const { data: versions, isLoading: versionsLoading } = useQuery({
        queryKey: ["admin-skill-versions", skillId],
        queryFn: () => getSkillVersions(skillId),
        enabled: open,
    });

    // Fetch current skill state (for "current" mode)
    const { data: currentSkill } = useQuery({
        queryKey: ["admin-skill-details", String(skillId)],
        queryFn: () => getSkill(skillId),
        enabled: open && diffMode === "current",
    });

    const orderedVersions = useMemo(
        () => (versions ? sortSkillVersionsByRecency(versions) : []),
        [versions]
    );

    // The version list shares its query key with the editor's always-on
    // versions query, so it stays readable from the cache while this modal is
    // closed. Without the open check, the reset below would let this effect
    // re-select from a stale initialVersionId before the next open supplies a
    // fresh one.
    useEffect(() => {
        if (!open || orderedVersions.length === 0 || selectedVersionId !== null)
            return;
        const requested = orderedVersions.find(
            (v) => v.id === initialVersionId
        );
        setSelectedVersionId(requested?.id ?? orderedVersions[0].id);
    }, [open, orderedVersions, selectedVersionId, initialVersionId]);

    // Reset selection when modal reopens
    useEffect(() => {
        if (!open) {
            setSelectedVersionId(null);
            setDiffMode("previous");
        }
    }, [open]);

    // Find selected version's minimal info
    const selectedVersion = useMemo(
        () => orderedVersions.find((v) => v.id === selectedVersionId),
        [orderedVersions, selectedVersionId]
    );

    // Find previous version ID (one with version_number - 1)
    const previousVersionId = useMemo(() => {
        if (!selectedVersion) return null;
        const prev = orderedVersions.find(
            (v) => v.version_number === selectedVersion.version_number - 1
        );
        return prev?.id ?? null;
    }, [orderedVersions, selectedVersion]);

    const previousVersionMissing =
        diffMode === "previous" &&
        selectedVersion !== undefined &&
        selectedVersion.version_number > 1 &&
        previousVersionId === null;

    // Fetch selected version detail
    const { data: selectedDetail, isLoading: selectedLoading } = useQuery({
        queryKey: ["admin-skill-version-detail", skillId, selectedVersionId],
        queryFn: () => getSkillVersion(skillId, selectedVersionId!),
        enabled: open && selectedVersion !== undefined,
        staleTime: Infinity,
    });

    // Fetch previous version detail (for "previous" mode)
    const { data: previousDetail, isLoading: previousLoading } = useQuery({
        queryKey: ["admin-skill-version-detail", skillId, previousVersionId],
        queryFn: () => getSkillVersion(skillId, previousVersionId!),
        enabled: open && diffMode === "previous" && previousVersionId !== null,
        staleTime: Infinity,
    });

    // Compute diff data
    const {
        oldName,
        newName,
        oldDescription,
        newDescription,
        oldContent,
        newContent,
        oldLabel,
        newLabel,
        fileChanges,
    } = useMemo(() => {
        const empty = {
            oldName: "",
            newName: "",
            oldDescription: "",
            newDescription: "",
            oldContent: "",
            newContent: "",
            oldLabel: "",
            newLabel: "",
            fileChanges: [] as FileChange[],
        };

        if (!selectedDetail) return empty;

        if (diffMode === "current") {
            if (!currentSkill) return empty;
            const oldFiles = selectedDetail.snapshot.files ?? [];
            const newFiles: ISkillFileSnapshot[] = currentSkill.skill_files.map(
                (f) => ({
                    name: f.name,
                    content: f.content,
                })
            );
            return {
                oldName: selectedDetail.snapshot.name,
                newName: currentSkill.name,
                oldDescription: selectedDetail.snapshot.description,
                newDescription: currentSkill.description,
                oldContent: selectedDetail.snapshot.content,
                newContent: currentSkill.content,
                oldLabel: `v${selectedDetail.version_number}`,
                newLabel: "Current",
                fileChanges: computeFileChanges(oldFiles, newFiles),
            };
        }

        // "previous" mode
        if (selectedVersion && selectedVersion.version_number === 1) {
            const newFiles = selectedDetail.snapshot.files ?? [];
            return {
                oldName: "",
                newName: selectedDetail.snapshot.name,
                oldDescription: "",
                newDescription: selectedDetail.snapshot.description,
                oldContent: "",
                newContent: selectedDetail.snapshot.content,
                oldLabel: "(empty)",
                newLabel: "v1",
                fileChanges: computeFileChanges([], newFiles),
            };
        }

        if (!previousDetail) return empty;

        const oldFiles = previousDetail.snapshot.files ?? [];
        const newFiles = selectedDetail.snapshot.files ?? [];
        return {
            oldName: previousDetail.snapshot.name,
            newName: selectedDetail.snapshot.name,
            oldDescription: previousDetail.snapshot.description,
            newDescription: selectedDetail.snapshot.description,
            oldContent: previousDetail.snapshot.content,
            newContent: selectedDetail.snapshot.content,
            oldLabel: `v${previousDetail.version_number}`,
            newLabel: `v${selectedDetail.version_number}`,
            fileChanges: computeFileChanges(oldFiles, newFiles),
        };
    }, [
        selectedDetail,
        previousDetail,
        diffMode,
        currentSkill,
        selectedVersion,
    ]);

    const isLoadingDiff =
        selectedLoading ||
        (diffMode === "previous" &&
            previousVersionId !== null &&
            previousLoading);

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            disableRestoreFocus
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                    px: 3,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography
                    component="span"
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: "1.125rem" }}
                >
                    Version History
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Close version history"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, overflow: "hidden" }}>
                {versionsLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            p: 4,
                        }}
                    >
                        <CircularProgress size={28} />
                    </Box>
                ) : orderedVersions.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            No published versions yet.
                        </Typography>
                    </Box>
                ) : (
                    <Stack
                        direction="row"
                        sx={{ height: "100%", overflow: "hidden" }}
                    >
                        {/* ── Left pane: version list ── */}
                        <Box
                            sx={{
                                width: 200,
                                minWidth: 200,
                                borderRight: `1px solid ${theme.palette.divider}`,
                                overflowY: "auto",
                            }}
                        >
                            <List disablePadding>
                                {orderedVersions.map((v) => (
                                    <ListItemButton
                                        key={v.id}
                                        selected={v.id === selectedVersionId}
                                        onClick={() =>
                                            setSelectedVersionId(v.id)
                                        }
                                        sx={{ py: 1.5, px: 2 }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                >
                                                    v{v.version_number}
                                                </Typography>
                                            }
                                            secondary={
                                                <Stack component="span">
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: "block",
                                                        }}
                                                    >
                                                        {v.publisher.first_name}{" "}
                                                        {v.publisher.last_name}
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        color="text.disabled"
                                                        sx={{
                                                            display: "block",
                                                        }}
                                                    >
                                                        {formatDistanceToNowStrict(
                                                            v.published_at,
                                                            {
                                                                addSuffix: true,
                                                            }
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            }
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Box>

                        {/* ── Right pane: diff viewer ── */}
                        <Stack
                            sx={{
                                flex: 1,
                                overflow: "hidden",
                            }}
                        >
                            {/* Toggle bar */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    bgcolor: "grey.50",
                                }}
                            >
                                <ToggleButtonGroup
                                    value={diffMode}
                                    exclusive
                                    onChange={(_, val) => {
                                        if (val) setDiffMode(val);
                                    }}
                                    size="small"
                                >
                                    <ToggleButton
                                        value="previous"
                                        sx={{
                                            textTransform: "none",
                                            px: 2,
                                            fontSize: "0.8125rem",
                                        }}
                                    >
                                        Changes from previous
                                    </ToggleButton>
                                    <ToggleButton
                                        value="current"
                                        sx={{
                                            textTransform: "none",
                                            px: 2,
                                            fontSize: "0.8125rem",
                                        }}
                                    >
                                        Compare with current
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {selectedVersion && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {diffMode === "previous"
                                            ? selectedVersion.version_number ===
                                              1
                                                ? "Showing v1 (initial version)"
                                                : previousVersionMissing
                                                  ? `v${selectedVersion.version_number - 1} is not retained`
                                                  : `v${selectedVersion.version_number - 1} \u2192 v${selectedVersion.version_number}`
                                            : `v${selectedVersion.version_number} \u2192 Current`}
                                    </Typography>
                                )}
                            </Stack>

                            {/* Diff content */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: "auto",
                                    p: 2,
                                }}
                            >
                                {isLoadingDiff ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            py: 6,
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : previousVersionMissing ? (
                                    <Box sx={{ py: 6, textAlign: "center" }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {`Version v${(selectedVersion?.version_number ?? 1) - 1} is not retained, so changes from the immediately preceding version cannot be shown. Use "Compare with current" instead.`}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Stack spacing={2}>
                                        {/* Name diff */}
                                        {oldName !== newName && (
                                            <Stack spacing={0.5}>
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={600}
                                                >
                                                    Name
                                                </Typography>
                                                <InlineDiffViewer
                                                    oldText={oldName}
                                                    newText={newName}
                                                    oldLabel={oldLabel}
                                                    newLabel={newLabel}
                                                />
                                            </Stack>
                                        )}

                                        {/* Description diff */}
                                        {oldDescription !== newDescription && (
                                            <Stack spacing={0.5}>
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={600}
                                                >
                                                    When to Use
                                                </Typography>
                                                <InlineDiffViewer
                                                    oldText={oldDescription}
                                                    newText={newDescription}
                                                    oldLabel={oldLabel}
                                                    newLabel={newLabel}
                                                />
                                            </Stack>
                                        )}

                                        {/* Instructions diff */}
                                        <Stack spacing={0.5}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                            >
                                                Instructions
                                            </Typography>
                                            <InlineDiffViewer
                                                oldText={oldContent}
                                                newText={newContent}
                                                oldLabel={oldLabel}
                                                newLabel={newLabel}
                                            />
                                        </Stack>

                                        {/* File changes */}
                                        {fileChanges.length > 0 && (
                                            <>
                                                <Divider />
                                                <Stack spacing={0.5}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        fontWeight={600}
                                                    >
                                                        Supporting Files
                                                    </Typography>
                                                    {fileChanges.map((fc) => (
                                                        <Accordion
                                                            key={fc.name}
                                                            disableGutters
                                                            sx={{
                                                                border: `1px solid ${theme.palette.divider}`,
                                                                borderRadius:
                                                                    "8px !important",
                                                                "&::before": {
                                                                    display:
                                                                        "none",
                                                                },
                                                                "&:not(:last-child)":
                                                                    {
                                                                        mb: 1,
                                                                    },
                                                            }}
                                                        >
                                                            <AccordionSummary
                                                                expandIcon={
                                                                    <ExpandMoreIcon />
                                                                }
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight={
                                                                            500
                                                                        }
                                                                        sx={{
                                                                            fontFamily:
                                                                                "monospace",
                                                                        }}
                                                                    >
                                                                        {
                                                                            fc.name
                                                                        }
                                                                    </Typography>
                                                                    <Chip
                                                                        label={
                                                                            STATUS_LABELS[
                                                                                fc
                                                                                    .status
                                                                            ]
                                                                        }
                                                                        size="small"
                                                                        color={
                                                                            STATUS_COLORS[
                                                                                fc
                                                                                    .status
                                                                            ]
                                                                        }
                                                                        variant="outlined"
                                                                        sx={{
                                                                            fontSize:
                                                                                "0.7rem",
                                                                            height: 20,
                                                                        }}
                                                                    />
                                                                </Stack>
                                                            </AccordionSummary>
                                                            <AccordionDetails
                                                                sx={{ pt: 0 }}
                                                            >
                                                                <InlineDiffViewer
                                                                    oldText={
                                                                        fc.oldContent
                                                                    }
                                                                    newText={
                                                                        fc.newContent
                                                                    }
                                                                />
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    ))}
                                                </Stack>
                                            </>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Stack>
                    </Stack>
                )}
            </DialogContent>
        </StyledDialog>
    );
};
